import React, { useState, useEffect, useCallback } from 'react';
import { getClientes360, getClientes360Pedidos } from '../../services/api';
import ProductImage from '../../components/ProductImage/ProductImage';
import styles from './Clientes360.module.css';

const ABAS = [
    { key: 'tray', label: 'Tray (Legado)', desc: 'Histórico do e-commerce Tray — contrato encerra este mês, dados congelados como acervo.' },
    { key: 'macro', label: 'Macro', desc: 'Fonte viva — clientes e pedidos do novo e-commerce Macro.' },
    { key: 'consolidado', label: 'Consolidado', desc: 'Cruzamento Tray + Macro por CPF/CNPJ e e-mail — a visão única do cliente.' }
];

const fmtMoeda = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('pt-BR');
};
const fmtDoc = (v) => {
    if (!v) return '—';
    const d = String(v).replace(/\D/g, '');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return v;
};
const linkWhatsApp = (tel) => {
    if (!tel) return null;
    const d = String(tel).replace(/\D/g, '');
    if (d.length < 10) return null;
    return `https://wa.me/55${d.length > 11 ? d.slice(-11) : d}`;
};

const Clientes360 = () => {
    const [aba, setAba] = useState('consolidado');
    const [busca, setBusca] = useState('');
    const [buscaDebounced, setBuscaDebounced] = useState('');
    const [apenasComCompra, setApenasComCompra] = useState(false);
    const [apenasCruzados, setApenasCruzados] = useState(false);
    const [ordenar, setOrdenar] = useState('ultima_compra');
    const [dir, setDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [dados, setDados] = useState({ clientes: [], kpis: null, paginacao: null });
    const [loading, setLoading] = useState(true);

    // Modal de compras
    const [clienteAberto, setClienteAberto] = useState(null);
    const [pedidos, setPedidos] = useState(null);
    const [loadingPedidos, setLoadingPedidos] = useState(false);
    const [pedidoExpandido, setPedidoExpandido] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setBuscaDebounced(busca), 400);
        return () => clearTimeout(t);
    }, [busca]);

    useEffect(() => { setPage(1); }, [aba, buscaDebounced, apenasComCompra, apenasCruzados, ordenar, dir]);

    const carregar = useCallback(async () => {
        setLoading(true);
        const resp = await getClientes360({
            fonte: aba,
            busca: buscaDebounced,
            apenas_com_compra: apenasComCompra,
            apenas_cruzados: apenasCruzados,
            ordenar, dir, page, limit: 50
        });
        setDados(resp);
        setLoading(false);
    }, [aba, buscaDebounced, apenasComCompra, apenasCruzados, ordenar, dir, page]);

    useEffect(() => { carregar(); }, [carregar]);

    const alternarOrdenacao = (campo) => {
        if (ordenar === campo) setDir(dir === 'asc' ? 'desc' : 'asc');
        else { setOrdenar(campo); setDir('desc'); }
    };

    const abrirCompras = async (cliente) => {
        setClienteAberto(cliente);
        setPedidos(null);
        setPedidoExpandido(null);
        setLoadingPedidos(true);
        const trayId = cliente.tray?.id || (aba === 'tray' ? cliente.id : null);
        const macroId = cliente.macro?.id || (aba === 'macro' ? cliente.id : null);
        const resp = await getClientes360Pedidos({ trayId, macroId });
        setPedidos(resp);
        setLoadingPedidos(false);
    };

    const fecharModal = () => { setClienteAberto(null); setPedidos(null); };

    const { clientes, kpis, paginacao } = dados;
    const abaAtual = ABAS.find(a => a.key === aba);
    const SetaOrdenacao = ({ campo }) => ordenar === campo
        ? <span className={styles.sortArrow}>{dir === 'asc' ? '▲' : '▼'}</span> : null;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Clientes 360</h1>
                <p className={styles.subtitulo}>
                    Todos os clientes e todas as compras — Tray (legado), Macro e a visão consolidada.
                </p>
            </div>

            <div className={styles.tabs}>
                {ABAS.map(a => (
                    <button
                        key={a.key}
                        className={`${styles.tab} ${aba === a.key ? styles.tabAtiva : ''}`}
                        onClick={() => setAba(a.key)}
                    >
                        {a.label}
                    </button>
                ))}
            </div>
            <div className={styles.abaDescricao}>
                {aba === 'tray' && <span className={styles.badgeLegado}>LEGADO</span>}
                {abaAtual.desc}
            </div>

            {kpis && (
                <div className={styles.kpis}>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiValor}>{kpis.total_clientes.toLocaleString('pt-BR')}</span>
                        <span className={styles.kpiLabel}>Clientes</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiValor}>{kpis.clientes_com_compra.toLocaleString('pt-BR')}</span>
                        <span className={styles.kpiLabel}>Com compra</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiValor}>{kpis.total_pedidos.toLocaleString('pt-BR')}</span>
                        <span className={styles.kpiLabel}>Pedidos</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiValor}>{fmtMoeda(kpis.receita_total)}</span>
                        <span className={styles.kpiLabel}>Receita total</span>
                    </div>
                    {aba === 'consolidado' && kpis.clientes_nas_duas != null && (
                        <div className={`${styles.kpiCard} ${styles.kpiDestaque}`}>
                            <span className={styles.kpiValor}>{kpis.clientes_nas_duas.toLocaleString('pt-BR')}</span>
                            <span className={styles.kpiLabel}>Nas duas bases</span>
                        </div>
                    )}
                </div>
            )}

            <div className={styles.filtros}>
                <input
                    className={styles.busca}
                    placeholder="Buscar por nome, e-mail, CPF/CNPJ ou telefone…"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                />
                <label className={styles.check}>
                    <input type="checkbox" checked={apenasComCompra} onChange={e => setApenasComCompra(e.target.checked)} />
                    Só com compra
                </label>
                {aba === 'consolidado' && (
                    <label className={styles.check}>
                        <input type="checkbox" checked={apenasCruzados} onChange={e => setApenasCruzados(e.target.checked)} />
                        Só quem está nas duas bases
                    </label>
                )}
            </div>

            <div className={styles.tabelaWrap}>
                {loading ? (
                    <div className={styles.loading}>Carregando clientes…</div>
                ) : (
                    <table className={styles.tabela}>
                        <thead>
                            <tr>
                                <th onClick={() => alternarOrdenacao('nome')}>Cliente <SetaOrdenacao campo="nome" /></th>
                                <th>CPF/CNPJ</th>
                                <th>Contato</th>
                                {aba === 'consolidado' && <th>Fontes</th>}
                                <th onClick={() => alternarOrdenacao('qtd_pedidos')} className={styles.thNum}>Pedidos <SetaOrdenacao campo="qtd_pedidos" /></th>
                                <th onClick={() => alternarOrdenacao('total_gasto')} className={styles.thNum}>Total gasto <SetaOrdenacao campo="total_gasto" /></th>
                                <th onClick={() => alternarOrdenacao('ultima_compra')}>Última compra <SetaOrdenacao campo="ultima_compra" /></th>
                                <th onClick={() => alternarOrdenacao('dias_sem_comprar')} className={styles.thNum}>Dias s/ comprar <SetaOrdenacao campo="dias_sem_comprar" /></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientes.map((c, i) => {
                                const wa = linkWhatsApp(c.telefone);
                                return (
                                    <tr key={`${c.id || c.email || c.cpf_cnpj || 'x'}-${i}`}>
                                        <td>
                                            <div className={styles.nome}>{c.nome || '—'}</div>
                                            {c.cidade && <div className={styles.cidade}>{c.cidade}{c.uf ? ` / ${c.uf}` : ''}</div>}
                                        </td>
                                        <td className={styles.mono}>{fmtDoc(c.cpf_cnpj)}</td>
                                        <td>
                                            {c.email && <div className={styles.email}>{c.email}</div>}
                                            {c.telefone && (
                                                wa
                                                    ? <a className={styles.whats} href={wa} target="_blank" rel="noopener noreferrer">{c.telefone}</a>
                                                    : <div>{c.telefone}</div>
                                            )}
                                            {!c.email && !c.telefone && '—'}
                                        </td>
                                        {aba === 'consolidado' && (
                                            <td>
                                                <span className={styles.fontes}>
                                                    {c.tray && <span className={styles.badgeTray}>Tray</span>}
                                                    {c.macro && <span className={styles.badgeMacro}>Macro</span>}
                                                </span>
                                            </td>
                                        )}
                                        <td className={styles.tdNum}>{c.qtd_pedidos || 0}</td>
                                        <td className={styles.tdNum}>{fmtMoeda(c.total_gasto)}</td>
                                        <td>{fmtData(c.ultima_compra)}</td>
                                        <td className={styles.tdNum}>{c.dias_sem_comprar ?? '—'}</td>
                                        <td>
                                            {(c.qtd_pedidos > 0) && (
                                                <button className={styles.btnCompras} onClick={() => abrirCompras(c)}>
                                                    Ver compras
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {clientes.length === 0 && (
                                <tr><td colSpan={9} className={styles.vazio}>Nenhum cliente encontrado.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {paginacao && paginacao.total_pages > 1 && (
                <div className={styles.paginacao}>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Anterior</button>
                    <span>Página {paginacao.page} de {paginacao.total_pages} — {paginacao.total.toLocaleString('pt-BR')} clientes</span>
                    <button disabled={page >= paginacao.total_pages} onClick={() => setPage(p => p + 1)}>Próxima ›</button>
                </div>
            )}

            {clienteAberto && (
                <div className={styles.modalOverlay} onClick={fecharModal}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>{clienteAberto.nome}</h2>
                                <div className={styles.modalSub}>
                                    {fmtDoc(clienteAberto.cpf_cnpj)} {clienteAberto.email ? `· ${clienteAberto.email}` : ''}
                                </div>
                            </div>
                            <button className={styles.btnFechar} onClick={fecharModal}>×</button>
                        </div>

                        {loadingPedidos ? (
                            <div className={styles.loading}>Carregando compras…</div>
                        ) : pedidos && (
                            <div className={styles.modalCorpo}>
                                {pedidos.resumo && (() => {
                                    const validos = pedidos.pedidos.filter(p => p.status !== 'Cancelado');
                                    const datas = validos.map(p => p.data).filter(Boolean).sort();
                                    const ticket = pedidos.resumo.total_pedidos > 0
                                        ? pedidos.resumo.total_gasto / pedidos.resumo.total_pedidos : 0;
                                    return (
                                        <div className={styles.kpisModal}>
                                            <div className={styles.kpiModalCard}>
                                                <span className={styles.kpiModalLabel}>Total Pedidos</span>
                                                <span className={`${styles.kpiModalValor} ${styles.kpiAzul}`}>{pedidos.resumo.total_pedidos}</span>
                                            </div>
                                            <div className={styles.kpiModalCard}>
                                                <span className={styles.kpiModalLabel}>Total Gasto (LTV)</span>
                                                <span className={`${styles.kpiModalValor} ${styles.kpiVerde}`}>{fmtMoeda(pedidos.resumo.total_gasto)}</span>
                                            </div>
                                            <div className={styles.kpiModalCard}>
                                                <span className={styles.kpiModalLabel}>Ticket Médio</span>
                                                <span className={`${styles.kpiModalValor} ${styles.kpiCiano}`}>{fmtMoeda(ticket)}</span>
                                            </div>
                                            <div className={styles.kpiModalCard}>
                                                <span className={styles.kpiModalLabel}>Primeira Compra</span>
                                                <span className={styles.kpiModalValor}>{fmtData(datas[0])}</span>
                                                {datas.length > 1 && <span className={styles.kpiModalSub}>Última: {fmtData(datas[datas.length - 1])}</span>}
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className={styles.modalGrid}>
                                    <aside className={styles.colunaEsquerda}>
                                        {pedidos.analise_categorias?.length > 0 && (
                                            <section className={styles.cartao}>
                                                <h3 className={styles.secaoTitulo}>
                                                    Análise por categoria
                                                    <span className={styles.secaoNota}>prefixo do SKU</span>
                                                </h3>
                                                <div className={styles.listaCategorias}>
                                                    {pedidos.analise_categorias.map(cat => (
                                                        <div key={cat.categoria} className={styles.catLinha}>
                                                            <div className={styles.catTopo}>
                                                                <span className={styles.catNome}>{cat.categoria}</span>
                                                                <span className={`${styles.pill} ${styles['pill_' + cat.status]}`}>
                                                                    {{ em_dia: 'Em dia', atrasada: 'Atrasada', quebrou: 'Quebrou padrão', esporadico: 'Esporádico' }[cat.status]}
                                                                </span>
                                                            </div>
                                                            <div className={styles.catDetalhe}>
                                                                {cat.compras} compra{cat.compras > 1 ? 's' : ''} · última {fmtData(cat.ultima_compra)}
                                                                {cat.dias_sem_comprar != null && (
                                                                    <> · <span className={cat.status === 'quebrou' ? styles.diasQuebrou : ''}>{cat.dias_sem_comprar}d</span></>
                                                                )}
                                                                <span className={styles.catTotal}>{fmtMoeda(cat.total)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        )}
                                    </aside>

                                    <div className={styles.colunaDireita}>
                                {pedidos.compras_por_mes?.length > 0 && (
                                    <section className={styles.cartao}>
                                        <h3 className={styles.secaoTitulo}>
                                            Compras por mês
                                            <span className={styles.secaoNota}>{pedidos.compras_por_mes.length} {pedidos.compras_por_mes.length === 1 ? 'mês' : 'meses'} com compra</span>
                                        </h3>
                                        <div className={styles.grafico}>
                                            {(() => {
                                                // Linha do tempo contínua: do primeiro ao último mês,
                                                // incluindo meses sem compra (limitada aos últimos 12).
                                                const porMes = Object.fromEntries(pedidos.compras_por_mes.map(m => [m.mes, m]));
                                                const ordenados = pedidos.compras_por_mes.map(m => m.mes).sort();
                                                const meses = [];
                                                let [ano, mes] = ordenados[0].split('-').map(Number);
                                                const fim = ordenados[ordenados.length - 1];
                                                while (true) {
                                                    const chave = `${ano}-${String(mes).padStart(2, '0')}`;
                                                    meses.push(porMes[chave] || { mes: chave, total: 0, pedidos: 0 });
                                                    if (chave === fim) break;
                                                    mes++; if (mes > 12) { mes = 1; ano++; }
                                                }
                                                const visiveis = meses.slice(-12);
                                                const max = Math.max(...visiveis.map(m => m.total), 1);
                                                return visiveis.map(m => (
                                                    <div
                                                        key={m.mes}
                                                        className={styles.barraCol}
                                                        title={m.total > 0
                                                            ? `${m.mes}: ${fmtMoeda(m.total)} (${m.pedidos} pedido${m.pedidos > 1 ? 's' : ''})`
                                                            : `${m.mes}: sem compra`}
                                                    >
                                                        <div className={styles.barraValor}>{m.total > 0 ? fmtMoeda(m.total) : '—'}</div>
                                                        {m.total > 0
                                                            ? <div className={styles.barra} style={{ height: `${Math.max(4, (m.total / max) * 100)}%` }} />
                                                            : <div className={styles.barraVazia} />}
                                                        <div className={styles.barraMes}>{m.mes.slice(5)}/{m.mes.slice(2, 4)}</div>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </section>
                                )}

                                <section className={styles.cartao}>
                                    <h3 className={styles.secaoTitulo}>Histórico de pedidos ({pedidos.pedidos.length})</h3>
                                <div className={styles.listaPedidos}>
                                    {pedidos.pedidos.map(p => (
                                        <div key={`${p.origem}-${p.id}`} className={styles.pedido}>
                                            <div
                                                className={styles.pedidoLinha}
                                                onClick={() => setPedidoExpandido(pedidoExpandido === `${p.origem}-${p.id}` ? null : `${p.origem}-${p.id}`)}
                                            >
                                                <span className={p.origem === 'tray' ? styles.badgeTray : styles.badgeMacro}>
                                                    {p.origem === 'tray' ? 'Tray' : 'Macro'}
                                                </span>
                                                <span className={styles.pedidoNumero}>#{p.numero}</span>
                                                <span>{fmtData(p.data)}</span>
                                                <span className={`${styles.pedidoStatus} ${p.status === 'Cancelado' ? styles.statusCancelado : ''}`}>{p.status || '—'}</span>
                                                <span className={styles.pedidoTotal}>{fmtMoeda(p.total)}</span>
                                                <span className={styles.expandir}>{pedidoExpandido === `${p.origem}-${p.id}` ? '▲' : '▼'}</span>
                                            </div>
                                            {pedidoExpandido === `${p.origem}-${p.id}` && (
                                                <div className={styles.itens}>
                                                    {p.itens.length === 0 && <div className={styles.vazio}>Sem itens registrados.</div>}
                                                    {p.itens.map((it, j) => (
                                                        <div key={j} className={styles.item}>
                                                            <ProductImage src={it.imagem_url} alt={it.descricao} className={styles.itemImg} />
                                                            <div className={styles.itemInfo}>
                                                                <span className={styles.itemDesc}>{it.descricao}</span>
                                                                {it.codigo_produto && <span className={styles.itemCodigo}>{it.codigo_produto}</span>}
                                                            </div>
                                                            <span className={styles.itemQtd}>{it.quantidade}×</span>
                                                            <span>{fmtMoeda(it.valor_unitario)}</span>
                                                            <span className={styles.itemTotal}>{fmtMoeda(it.valor_total)}</span>
                                                        </div>
                                                    ))}
                                                    {(p.pagamento || p.frete != null) && (
                                                        <div className={styles.pedidoExtras}>
                                                            {p.pagamento && <span>Pagamento: {p.pagamento}{p.parcelas > 1 ? ` (${p.parcelas}×)` : ''}</span>}
                                                            {p.frete != null && <span>Frete: {fmtMoeda(p.frete)}</span>}
                                                            {p.descontos > 0 && <span>Descontos: {fmtMoeda(p.descontos)}</span>}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {pedidos.pedidos.length === 0 && <div className={styles.vazio}>Nenhuma compra registrada.</div>}
                                </div>
                                </section>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clientes360;
